import {
  CSSProperties,
  FC,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import SearchHotel from '../Search/SearchHotel';
import { applyPollSupplierCounts, beginHotelSearch, getHotels, getSerpMatchingData, stopPolling } from 'services/hotel';
import { SearchHotelModel } from 'ts/hotel/classes/SearchHotelModel';
import {
  AvailableBoardType,
  GoToRoomDetailsParams,
  Hotel,
  HotelResponse,
  HotelStatistics,
  NeighborhoodHotel,
} from 'ts/hotel/interfaces/HotelResponse';
import HotelsList from './Cards/HotelsList';
import SkeletonHotelsList from './Cards/ResultsListSkeleton';
import FilterResults from './Filters/FilterResults';
import { FilterModel, LocationValue } from 'ts/hotel/classes/FilterModel';
import { HotelFilterContext } from 'contexts/HotelFilterContext';
import { getDistanceByCoords, getCurrencyStr, openInNewWindow } from 'utils/utils';
import ResultsFilterSkeleton from './Filters/ResultsFilterSkeleton';
import { MapHotelContext } from 'contexts/MapHotelContext';
import {
  applyHTypeCounts,
  formatHotels,
  getAllHotelsWithoutDuplicates,
  getFilterWithPreviousValues,
  getFilteredHotels,
  getHotelDisplaySettings,
  getHotelIndex,
  getHotelNameFromHotelSearchGA,
  getKey,
  getMaxOccupancy,
  getOriginalHotelKey,
  getRoomsBTokensForDetails,
  getSerpColorCounts,
  getSortByFullName,
  getSortedHotels,
  handleSerpHotel,
  mergeFilters,
  pickHotelsByBpids,
  upsertHotelsByBpid,
} from 'utils/hotel';
import useRecaptcha from 'hooks/useRecaptcha';
import { FocusedMarker, Position } from 'ts/hotel/interfaces/Generic';
import ExpiredTimeout from 'components/shared/ExpiredTimeout';
import { dateFormat } from 'utils/shared/utils';
import usePageInMemory from 'hooks/usePageInMemory';
import withErrorBoundary from 'components/shared/withErrorBoundary';
import { useGetPage } from 'hooks/useGetPage';
import ResultHotelConfig from './HotelResultConfig';
import { GeoPolygon } from 'ts/hotel/interfaces/HotelDetails';

import { TripAdvisorData } from 'ts/hotel/interfaces/TripadvisorResponse';
import { SerpData, SerpResult } from 'ts/hotel/interfaces/SerpResponse';

import FilterResultLeft from './Filters/FilterResultLeft';
import AssistantPageBridge from 'components/assistant/AssistantPageBridge';
import ResultSplitter, { useLeftPanelWidth } from './ResultSplitter';
import useHotelDataLayer from 'hooks/useHotelDataLayer';
import ResultHotelTabs from './ResultHotelTabs';
import {
  applyHotelCardAction,
  bringHotelToFront,
  cardActionNeedsDom,
  findHotelForCardAction,
  HotelCardActionPayload,
  revealHotelForCardAction,
  scrollHotelCardIntoView,
} from './hotelCardActions';
import {
  getPendingCardActionsVersion,
  getPendingResultLocationsVersion,
  hasPendingHotelCardAction,
  peekPendingHotelCardAction,
  peekPendingResultLocation,
  publishLocalHotelCardAction,
  setActiveHotelForAssistant,
  setPageHotelsForAssistant,
  subscribeToPendingCardActions,
  subscribeToPendingResultLocations,
  takePendingHotelCardAction,
  takePendingResultLocation,
} from 'utils/assistantChatStore';
import { useHotelProposal } from 'hooks/useProposal';
import { MainContext } from 'contexts/MainContext';
import { HotelConfigContext } from 'contexts/HotelConfigContext';
import { b2b, isFreeCxlDefault, defaultSearchTypeId } from 'constants/generic';

const ResultPage: FC = () => {
  const { handleRecapcha, isRecaptchaReady } = useRecaptcha();
  const [hotels, setHotels] = useState<Hotel[]>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [filter, setFilter] = useState<FilterModel>();
  const [availableBoardTypes, setAvailableBoardTypes] = useState<AvailableBoardType[]>();
  const [total, setTotal] = useState(0);
  const [poolingFinished, setPoolingFinished] = useState(false);
  const { width: leftPanelWidth, setWidth: setLeftPanelWidth, reset: resetLeftPanelWidth } = useLeftPanelWidth();
  const [response, setResponse] = useState<HotelResponse>();
  const [isInit, setIsInit] = useState(true);
  const [focusedHotel, setFocusedHotel] = useState<FocusedMarker | undefined>();
  const [centered, setCentered] = useState<Position | undefined>();
  const [initedMap, setInitedMap] = useState(false);
  const { hotelIndex } = useContext(HotelConfigContext);
  const { userSiteConfig, user } = useContext(MainContext);
  const { toggleHotelProposal, toggleRoomProposal } = useHotelProposal();
  const cardActionVersion = useSyncExternalStore(
    subscribeToPendingCardActions,
    getPendingCardActionsVersion,
  );
  const resultLocationVersion = useSyncExternalStore(
    subscribeToPendingResultLocations,
    getPendingResultLocationsVersion,
  );

  const [currency, setCurrency] = useState(() => getHotelDisplaySettings(hotelIndex).currency);
  const [priceMode, setPriceMode] = useState(() => getHotelDisplaySettings(hotelIndex).priceMode);
  const fetchRequest = useRef(new AbortController());
  const delayPromiseReject = useRef<any>();
  const fetchTimeout = useRef<any>();
  const filterRef = useRef(filter);
  filterRef.current = filter;
  const allHotelsRef = useRef<Hotel[]>([]);
  const availableBoardTypesRef = useRef<AvailableBoardType[]>([]);
  const [showExpiredModal, setShowExpiredModal] = useState(false);
  const defaultActiveTab = 6;
  const [activeTab, setActiveTab] = useState(defaultActiveTab);
  const [pinnedHotelsIds, setPinnedHotelsIds] = useState<string[]>([]);
  const showModalTimeoutRef = useRef<any>();
  const [activeHotel, setActiveHotel] = useState<Hotel | undefined>();
  const isMainLoading = !hotels?.length && !response?.sessionExpired && !poolingFinished;

  const location = useLocation();
  const isFirstLoad = useRef(true);
  const [geoPolygons, setGeoPolygons] = useState<GeoPolygon[][]>();
  const defaultAreRoomsFiltered = userSiteConfig.hotel.areRoomsFiltered;
  const areRoomsFiltered = user?.branch?.hotelDisplaySupplierName ? defaultAreRoomsFiltered : true;
  const isHotelNameSearch = !!(
    searchParams.get('selectedHotelName') || getHotelNameFromHotelSearchGA()
  );
  const defaultSortBy = isHotelNameSearch ? 'distanceIncrease' : 'recommended';
  const defaultSortByIfEmpty = defaultSortBy;
  const sortBy = useRef(defaultSortBy);
  const [sortByState, setSortByState] = useState(defaultSortBy);
  const [gh, setGh] = useState<SerpData[]>([]);
  const [ta, setTa] = useState<TripAdvisorData[]>([]);
  const sortByFullName = useRef('');
  const serpInterval = useRef<any>();
  const serpResults = useRef<SerpResult[]>([]);
  const navigate = useNavigate();
  const { openDetailsInNewTab } = userSiteConfig.base;
  const [neighborhoodList, setNeighborhoodList] = useState<NeighborhoodHotel[]>([]);
  const isModernDisplayMode = userSiteConfig.hotel.displayStyle === '2' && window.innerWidth > 768;
  const [areActiveRooms, setAreActiveRooms] = useState(false);
  const [stat, setStat] = useState<HotelStatistics>({
    serp: {
      totalResults: 0,
      green: 0,
      orange: 0,
      red: 0,
      isLoading: true,
    },
  });
  const stateRef = useRef<any>();
  const idbKey = location.pathname + location.search;
  const [resetLocation, setResetLocation] = useState(new Date());
  const { pushImpression } = useHotelDataLayer();
  const timeoutMs = useRef(2000);
  const latestSearchRef = useRef('');
  const isLoggedRef = useRef(false);
  const impressionSentRef = useRef(false);
  const initedSearchParamsRef = useRef('');
  const pinnedHotelsIdsRef = useRef(pinnedHotelsIds);
  pinnedHotelsIdsRef.current = pinnedHotelsIds;
  const neighborhoodListRef = useRef(neighborhoodList);
  neighborhoodListRef.current = neighborhoodList;
  const currencyRef = useRef(currency);
  currencyRef.current = currency;
  const activeHotelRef = useRef(activeHotel);
  activeHotelRef.current = activeHotel;
  const focusedHotelRef = useRef(focusedHotel);
  focusedHotelRef.current = focusedHotel;
  const poolingFinishedRef = useRef(poolingFinished);
  poolingFinishedRef.current = poolingFinished;
  stateRef.current = {
    hotels,
    activeHotel,
    activeTab,
    focusedHotel,
    currency,
    priceMode,
    filter,
    total,
    availableBoardTypes,
    sortBy: sortBy.current,
    geoPolygons,
    initedMap,
    poolingFinished,
    response,
    gh,
    ta,
    serpResults,
    pinnedHotelsIds,
    stat,
  };

  const getActiveGeoPolygons = () =>
    neighborhoodListRef.current
      .filter((nb) => nb.active && nb.geoPolygon?.length)
      .flatMap((nb) => nb.geoPolygon || []);

  const buildDisplayHotels = (sourceHotels: Hotel[], nextFilter: FilterModel, locationName?: string) => {
    const filtered = getFilteredHotels(
      nextFilter,
      currencyRef.current,
      sourceHotels,
      getActiveGeoPolygons(),
      pinnedHotelsIdsRef.current,
    );

    if (nextFilter.name) {
      sortByFullName.current = getSortByFullName(filtered, nextFilter);
    }

    let sorted = getSortedHotels(
      filtered,
      sortBy.current || defaultSortByIfEmpty,
      sortByFullName.current,
      locationName,
      areRoomsFiltered,
      hotelIndex,
    );

    const focused = focusedHotelRef.current;
    if (focused) {
      const focusedHotelIndex = sorted.findIndex((h) => h.htuKey === focused.htuKey);
      if (focusedHotelIndex !== -1) {
        const next = sorted.slice();
        const item = { ...next[focusedHotelIndex], visible: true };
        next.splice(focusedHotelIndex, 1);
        next.unshift(item);
        sorted = next;
      }
    }

    return sorted;
  };

  const syncActiveHotelRooms = (displayHotels: Hotel[]) => {
    const current = activeHotelRef.current;
    if (!current) return;

    const updated = displayHotels.find((h) => h.htuKey === current.htuKey);
    if (
      updated?.roomClasses.length &&
      updated.roomClasses.length > (current.roomClasses.length || 0)
    ) {
      setActiveHotel({ ...current, roomClasses: updated.roomClasses });
    }
  };

  useGetPage('Hotels/Search');
  usePageInMemory(idbKey, () => { });

  useEffect(() => {
    if (!isRecaptchaReady) return;

    if (
      b2b &&
      (location.search.includes('details') || latestSearchRef.current === location.search)
    ) {
      return;
    }

    const expectedPathname = getKey('/hotels', hotelIndex) + '/results';
    const isLocationValid = location.pathname === expectedPathname;
    const isRememberedData = userSiteConfig.base.rememberModeEnabled && initedSearchParamsRef.current === location.search;

    if (!isLocationValid || isRememberedData) {
      return;
    }
    initedSearchParamsRef.current = location.search;

    sortByFullName.current = '';
    allHotelsRef.current = [];
    availableBoardTypesRef.current = [];
    setHotels(undefined);
    setFilter(undefined);
    setAvailableBoardTypes(undefined);
    setResponse(undefined);
    setActiveHotel(undefined);
    setGeoPolygons(undefined);
    isFirstLoad.current = false;
    sortBy.current = defaultSortBy;
    setSortByState(defaultSortBy);
    setPoolingFinished(false);
    clearTimeout(fetchTimeout.current);
    clearInterval(serpInterval.current);
    serpResults.current = [];
    setStat({
      serp: {
        totalResults: 0,
        green: 0,
        orange: 0,
        red: 0,
        isLoading: true,
      },
    });
    isLoggedRef.current = false;
    impressionSentRef.current = false;
    latestSearchRef.current = location.search;
    sessionStorage.setItem(getKey('lasestSearchUrl', hotelIndex), location.search);
    const needToSearch = searchParams.get('needToSearch');
    if (needToSearch === 'false') {
      setPoolingFinished(true);

      const element = document.querySelector('.search-wrapper') as HTMLElement;
      element?.classList.add('visible-responsive');
      return;
    }

    const filterHotels = sessionStorage.getItem(getKey('filterHotels', hotelIndex));
    const prevFilter = filterHotels ? JSON.parse(filterHotels) : undefined;
    if (filterHotels && prevFilter?.searchWithActiveFilters === false) {
      const newFilter = new FilterModel(
        hotels || [],
        availableBoardTypes || [],
        currency,
        !!isFreeCxlDefault,
        hotelIndex
      );

      sessionStorage.setItem(getKey('filterHotels', hotelIndex), JSON.stringify(newFilter));
    }

    (async () => {
      const recapchaToken = await handleRecapcha();
      if (!recapchaToken) {
        return;
      }

      if (isInit) {
        setIsInit(false);
      } else {
        setTotal(0);
        setPoolingFinished(false);
      }

      fetchRequest.current.abort();
      fetchRequest.current = new AbortController();
      const searchParamsObj = new SearchHotelModel(searchParams);

      const gaStr = sessionStorage.getItem(getKey('hotelSearchGA', hotelIndex));
      const gaObj = gaStr ? JSON.parse(gaStr) : undefined;
      const paramsJson = {
        ...searchParamsObj,
        recaptchaToken: recapchaToken,
        checkIn: dateFormat(searchParamsObj.checkIn, 'yyyy-MM-dd', 'en'),
        checkOut: dateFormat(searchParamsObj.checkOut, 'yyyy-MM-dd', 'en'),
        searchTypeId: userSiteConfig.hotel?.searchTypeId ? +userSiteConfig.hotel.searchTypeId : defaultSearchTypeId,
        googleAutocomplete: gaObj,
      };

      const paramsCleaned: any = { ...paramsJson };
      delete paramsCleaned.recaptchaToken;
      delete paramsCleaned.selectedHotelName;
      delete paramsCleaned.types;
      setStat((prev) => ({
        ...prev,
        query: JSON.stringify(paramsCleaned, null, 2),
      }));

      const beginRes = await beginHotelSearch(paramsJson, fetchRequest.current, hotelIndex);

      setCurrency(sessionStorage.getItem(getKey('displayCurrencyHotels', hotelIndex)) || 'usd');

      if (beginRes) {
        setStat((prev) => ({
          ...prev,
          sysToken: beginRes?.sysToken || '',
          searchToken: beginRes?.searchToken || '',
          geoPolygons: beginRes?.geoPolygons,
          geoSearch: beginRes?.geoSearch,
        }));
        setGeoPolygons(beginRes?.geoPolygons);
        setNeighborhoodList(beginRes?.neighborhoodList || []);

        await getHotelsRecursive(false, beginRes.searchToken, beginRes.sysToken || '');

        serpInterval.current = setInterval(async () => {
          const serpMatchingData = await getSerpMatchingData(
            sessionStorage.getItem(getKey('searchTokenHotels', hotelIndex)) || '',
            fetchRequest.current
          );

          if (serpMatchingData?.hasErrors || serpMatchingData?.poolingFinished) {
            clearInterval(serpInterval.current);

            setStat((prev) => ({
              ...prev,
              serp: {
                ...prev.serp,
                isLoading: false,
              },
            }));
          } else if (serpMatchingData?.serpJson) {
            const serpJson = JSON.parse(serpMatchingData.serpJson);

            const newSerpResult = serpJson
              ?.filter(
                (item: any) =>
                  item.target_bpid !== 'notfound' &&
                  item.total_rate?.lowest &&
                  item.total_rate?.extracted_lowest
              )
              .map((item: any) => ({
                bpid: item.target_bpid,
                lowest: item.total_rate?.extracted_lowest,
                currency: getCurrencyStr(item.total_rate?.lowest?.replace(/[0-9, ]/g, '')),
              }));

            serpResults.current = [...(serpResults.current || []), ...newSerpResult];

            setStat((prev) => ({
              ...prev,
              serp: {
                ...prev.serp,
                totalResults: serpResults.current.length,
              },
            }));

            if (newSerpResult.length && poolingFinishedRef.current) {
              allHotelsRef.current = allHotelsRef.current.map((hotel) =>
                handleSerpHotel(hotel, serpResults.current),
              );
              const counts = getSerpColorCounts(allHotelsRef.current);
              setStat((prev) => ({
                ...prev,
                serp: {
                  ...prev.serp,
                  ...counts,
                },
              }));
              setHotels((prev) =>
                prev?.map((hotel) => handleSerpHotel(hotel, serpResults.current)),
              );
            }
          }
        }, 2500);
      }
    })();

    clearTimeout(showModalTimeoutRef.current);
    setShowExpiredModal(false);
    showModalTimeoutRef.current = setTimeout(() => {
      setShowExpiredModal(true);
    }, 20 * 60 * 1000);
  }, [searchParams, isRecaptchaReady]); // eslint-disable-line

  useEffect(() => {
    return () => {
      fetchRequest.current.abort();
      clearTimeout(fetchTimeout.current);
      clearInterval(serpInterval.current);
      clearTimeout(showModalTimeoutRef.current);
      try {
        delayPromiseReject.current?.();
      } catch (_) {}
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      if (!response?.hotels?.length || fetchRequest.current.signal.aborted) {
        return;
      }

      const searchKey = latestSearchRef.current;
      const batchHotels = await formatHotels(response.hotels, hotelIndex);

      if (
        cancelled ||
        searchKey !== latestSearchRef.current ||
        fetchRequest.current.signal.aborted
      ) {
        return;
      }

      const locationName = searchParams.get('selectedHotelName') || '';
      const isFirstBatch = allHotelsRef.current.length === 0;

      allHotelsRef.current = getAllHotelsWithoutDuplicates(
        allHotelsRef.current,
        batchHotels,
        areRoomsFiltered,
      );

      const batchBpids = new Set(
        batchHotels
          .map((hotel) => hotel.item?.bpid)
          .filter((bpid): bpid is string => !!bpid),
      );

      if (serpResults.current.length && batchBpids.size) {
        allHotelsRef.current = allHotelsRef.current.map((hotel) => {
          const bpid = hotel.item?.bpid;
          if (!bpid || !batchBpids.has(bpid)) return hotel;
          return handleSerpHotel(hotel, serpResults.current);
        });
      }

      const serpCounts = getSerpColorCounts(allHotelsRef.current);
      setStat((prev) => ({
        ...prev,
        serp: {
          ...prev.serp,
          ...serpCounts,
        },
      }));

      const batchBoardTypes = response.availableBoardTypes || [];
      const newBoardTypes = batchBoardTypes.filter(
        (item) => !availableBoardTypesRef.current.some((board) => board.code === item.code),
      );
      availableBoardTypesRef.current = [...availableBoardTypesRef.current, ...newBoardTypes];

      try {
        const batchFilter = new FilterModel(
          batchHotels,
          availableBoardTypesRef.current,
          currencyRef.current,
          !!isFreeCxlDefault,
          hotelIndex,
        );

        let nextFilter: FilterModel;
        if (isFirstBatch) {
          const prevFilterStr = sessionStorage.getItem(getKey('filterHotels', hotelIndex));
          nextFilter = applyHTypeCounts(
            applyPollSupplierCounts(
              prevFilterStr
                ? getFilterWithPreviousValues(batchFilter, JSON.parse(prevFilterStr))
                : batchFilter,
            ),
            allHotelsRef.current,
          );
        } else {
          nextFilter = applyHTypeCounts(
            applyPollSupplierCounts(
              mergeFilters(filterRef.current || batchFilter, batchFilter) || batchFilter,
            ),
            allHotelsRef.current,
          );
        }

        filterRef.current = nextFilter;
        sessionStorage.setItem(getKey('filterHotels', hotelIndex), JSON.stringify(nextFilter));
        setFilter(nextFilter);
        setAvailableBoardTypes(availableBoardTypesRef.current);
        setTotal(allHotelsRef.current.length);

        const isPollComplete = !!response.poolingFinished || poolingFinishedRef.current;
        const useFullPipeline =
          isFirstBatch || isPollComplete || !!nextFilter.name?.trim();

        if (useFullPipeline) {
          const displayHotels = buildDisplayHotels(
            allHotelsRef.current,
            nextFilter,
            locationName,
          );
          setHotels(displayHotels);

          if (isFirstBatch && displayHotels.length && !activeHotelRef.current) {
            setActiveHotel(displayHotels[0]);
          } else {
            syncActiveHotelRooms(displayHotels);
          }

          if (isPollComplete && !impressionSentRef.current) {
            impressionSentRef.current = true;
            pushImpression(allHotelsRef.current);
          }
        } else {
          const affectedHotels = pickHotelsByBpids(allHotelsRef.current, batchBpids);
          const filteredDelta = getFilteredHotels(
            nextFilter,
            currencyRef.current,
            affectedHotels,
            getActiveGeoPolygons(),
            pinnedHotelsIdsRef.current,
          );

          setHotels((prev) => {
            const prevHotels = prev || [];
            const existingBpids = new Set(
              prevHotels
                .map((hotel) => hotel.item?.bpid)
                .filter((bpid): bpid is string => !!bpid),
            );
            const toUpsert = filteredDelta.filter(
              (hotel) => hotel.visible || (!!hotel.item?.bpid && existingBpids.has(hotel.item.bpid)),
            );
            const merged = upsertHotelsByBpid(prevHotels, toUpsert);
            const sorted = getSortedHotels(
              merged,
              sortBy.current || defaultSortByIfEmpty,
              sortByFullName.current,
              locationName,
              areRoomsFiltered,
              hotelIndex,
            );
            syncActiveHotelRooms(sorted);
            return sorted;
          });
        }
      } catch (e) {
        setPoolingFinished(true);
        console.log('GetHotelsRecursive', e);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [response?.hotels]); // eslint-disable-line

  useEffect(() => {
    if (!poolingFinished || !allHotelsRef.current.length || !filterRef.current) {
      return;
    }

    const locationName = searchParams.get('selectedHotelName') || '';
    const nextFilter = applyHTypeCounts(
      applyPollSupplierCounts(filterRef.current),
      allHotelsRef.current,
    );
    filterRef.current = nextFilter;
    setFilter(nextFilter);
    sessionStorage.setItem(getKey('filterHotels', hotelIndex), JSON.stringify(nextFilter));

    const displayHotels = buildDisplayHotels(allHotelsRef.current, nextFilter, locationName);
    setHotels(displayHotels);
    setTotal(allHotelsRef.current.length);
    syncActiveHotelRooms(displayHotels);

    if (!impressionSentRef.current) {
      impressionSentRef.current = true;
      pushImpression(allHotelsRef.current);
    }
  }, [poolingFinished]); // eslint-disable-line

  useEffect(() => {
    if (!focusedHotel) return;

    const focusedHotelIndex = hotels?.findIndex((h) => h.htuKey === focusedHotel.htuKey);
    const element = document.getElementById(focusedHotel.htuKey || '');

    if (!element && hotels && focusedHotelIndex !== undefined && focusedHotelIndex !== -1) {
      const newHotels = hotels.slice();
      const focused = { ...newHotels[focusedHotelIndex], visible: true };
      newHotels.splice(focusedHotelIndex, 1);
      newHotels.unshift(focused);
      setHotels(newHotels);
    }

    const scrollTimeout = window.setTimeout(() => {
      if (focusedHotel.htuKey) {
        scrollHotelCardIntoView(focusedHotel.htuKey);
      }
    }, 200);

    return () => window.clearTimeout(scrollTimeout);
  }, [focusedHotel]); // eslint-disable-line

  const getHotelsRecursive = async (poolingFinished: boolean, searchToken: string, sysToken: string) => {
    setPoolingFinished(poolingFinished);

    if (!poolingFinished) {
      const response = await getHotels({ searchToken, pageSize: 0, sysToken }, fetchRequest.current);

      if (response && !response?.sessionExpired) {
        let finalResponse: HotelResponse = response;

        if ((response.poolingCountAvailable ?? 0) > 0) {
          const additionalCallsCount = Math.min(4, response.poolingCountAvailable ?? 0);
          const additionalResponses = await Promise.all(
            Array.from({ length: additionalCallsCount }, () =>
              getHotels({ searchToken, pageSize: 0, sysToken }, fetchRequest.current)
            )
          );

          const validResponses = [response, ...additionalResponses].filter(
            (r): r is HotelResponse => !!r && !r.sessionExpired
          );

          if (validResponses.length > 0) {
            const allHotels = validResponses.reduce<Hotel[]>(
              (acc, r) => getAllHotelsWithoutDuplicates(acc, r.hotels || [], areRoomsFiltered),
              []
            );

            const allBoardTypes = validResponses.reduce<AvailableBoardType[]>((acc, r) => {
              const newTypes =
                r.availableBoardTypes?.filter(
                  (item) => !acc.some((board) => board.code === item.code)
                ) || [];
              return [...acc, ...newTypes];
            }, []);

            const lastResponse = validResponses[validResponses.length - 1];

            finalResponse = {
              ...lastResponse,
              hotels: allHotels,
              availableBoardTypes: allBoardTypes,
              totalHotelsCount: allHotels.length,
            };
          }
        }

        setResponse(finalResponse);
        if (finalResponse.hotels?.length && !isLoggedRef.current) {
          isLoggedRef.current = true;
          setStat((prev) => ({
            ...prev,
            startTime: new Date(),
          }));
        } else if (finalResponse.hotels?.length) {
          setStat((prev) => ({
            ...prev,
            endTime: new Date(),
          }));
        }

        const searchLogsArr = Object.entries((finalResponse as any).searchLogs || {}).map(([key, val]) => ({ name: key, val }));
        setStat((prev) => ({
          ...prev,
          searchLogs: [...(prev.searchLogs || []), ...searchLogsArr],
        }));

        try {
          await new Promise((resolve, reject) => {
            fetchTimeout.current = setTimeout(async () => {
              resolve(true);
              const pollingFinished = finalResponse.poolingFinished || false;
              await getHotelsRecursive(pollingFinished, searchToken, sysToken);
            }, timeoutMs.current);

            delayPromiseReject.current = reject;
          });
        } catch (error) { }
      } else if (response?.sessionExpired) {
        const newsearchParams = new URLSearchParams(searchParams);
        newsearchParams.set('pageRefresh', new Date().getTime().toString());
        setSearchParams(newsearchParams);
      } else if (response) {
        setPoolingFinished(true);
        setResponse(response);
      }
    }
  };

  const onFilterChange = (newFitler: FilterModel) => {
    filterRef.current = newFitler;
    setFilter(newFitler);
    sessionStorage.setItem(getKey('filterHotels', hotelIndex), JSON.stringify(newFitler));

    const filteredSortedHotels = buildDisplayHotels(allHotelsRef.current, newFitler);

    if (activeHotelRef.current) {
      const visibleHotels = filteredSortedHotels.filter((h) => h.visible);
      if (visibleHotels.length) {
        setActiveHotel(visibleHotels[0]);
      }
    }

    setHotels(filteredSortedHotels);
  };

  useEffect(() => {
    if (!filter) {
      return;
    }

    if (!pinnedHotelsIds.length) {
      if (filter.pinOnly) {
        onFilterChange({ ...filter, pinOnly: false });
      }
      return;
    }

    if (!filter.pinOnly) {
      return;
    }

    onFilterChange(filter);
  }, [pinnedHotelsIds]); // eslint-disable-line react-hooks/exhaustive-deps

  const onLocationChange = (newLocation: LocationValue) => {
    const searchLat = parseFloat(searchParams.get('lat') || '0');
    const searchLng = parseFloat(searchParams.get('lon') || '0');
    const isClear = !newLocation?.name || !newLocation?.lat;
    const centerLat = isClear ? searchLat : newLocation.lat;
    const centerLng = isClear ? searchLng : newLocation.lng;
    if (!centerLat && !centerLng) return;

    sortBy.current = 'distanceIncrease';
    setSortByState('distanceIncrease');

    allHotelsRef.current = allHotelsRef.current.map((hotel) => ({
      ...hotel,
      distance: getDistanceByCoords(
        centerLat,
        centerLng,
        hotel.position?.latitude || 0,
        hotel.position?.longitude || 0,
      ),
    }));

    const prevFilter = filterRef.current;
    if (!prevFilter) {
      setHotels(
        getSortedHotels(
          allHotelsRef.current,
          'distanceIncrease',
          sortByFullName.current,
          undefined,
          areRoomsFiltered,
          hotelIndex,
        ),
      );
      return;
    }

    const nextFilter: FilterModel = {
      ...prevFilter,
      location: {
        ...prevFilter.location,
        value: isClear ? { lat: 0, lng: 0, name: '' } : newLocation,
        km: isClear ? prevFilter.location.km : newLocation.km ?? prevFilter.location.km,
        isHotel: isClear ? undefined : newLocation.isHotel,
      },
    };

    filterRef.current = nextFilter;
    setFilter(nextFilter);
    sessionStorage.setItem(getKey('filterHotels', hotelIndex), JSON.stringify(nextFilter));
    setHotels(buildDisplayHotels(allHotelsRef.current, nextFilter));
  };

  useEffect(() => {
    const chatKey = hotelIndex ?? 0;
    const pending = peekPendingResultLocation(chatKey);
    if (!pending) {
      return;
    }
    takePendingResultLocation(chatKey);

    if (pending.clear) {
      setCentered(undefined);
      setResetLocation(new Date());
      return;
    }

    if (
      typeof pending.lat !== 'number' ||
      typeof pending.lng !== 'number' ||
      !pending.name
    ) {
      return;
    }

    const value: LocationValue = {
      name: pending.name,
      lat: pending.lat,
      lng: pending.lng,
      km: pending.km ?? undefined,
    };
    setCentered({ lat: pending.lat, lng: pending.lng, name: pending.name });
    onLocationChange(value);
  }, [resultLocationVersion]); // eslint-disable-line react-hooks/exhaustive-deps

  const onSortChange = (sort: string) => {
    sortBy.current = sort;
    setSortByState(sort);
    if (!filterRef.current) {
      setHotels(
        getSortedHotels(
          allHotelsRef.current,
          sort,
          sortByFullName.current,
          undefined,
          areRoomsFiltered,
          hotelIndex,
        ),
      );
      return;
    }
    setHotels(buildDisplayHotels(allHotelsRef.current, filterRef.current));
  };

  const onClickStop = () => {
    setPoolingFinished(true);
    fetchRequest.current.abort();
    clearTimeout(fetchTimeout.current);
    clearInterval(serpInterval.current);
    stopPolling(stat?.sysToken || '');

    const latestHotelPathStr = sessionStorage.getItem(getKey('latestHotelPath', hotelIndex));
    if (latestHotelPathStr) {
      const latestHotelPath = JSON.parse(latestHotelPathStr);
      sessionStorage.setItem(
        getKey('latestHotelPath', hotelIndex),
        JSON.stringify(latestHotelPath)
      );
    }
    if (!hotels) {
      setHotels([]);
    }
  };

  useEffect(() => {
    const chatKey = hotelIndex ?? 0;
    const pinnedSet = new Set(pinnedHotelsIds);
    const ordered = (hotels || [])
      .filter((hotel) => hotel.visible !== false)
      .sort((a, b) => {
        const aPinned = pinnedSet.has(getOriginalHotelKey(a.htuKey)) ? 0 : 1;
        const bPinned = pinnedSet.has(getOriginalHotelKey(b.htuKey)) ? 0 : 1;
        return aPinned - bPinned;
      })
      .map((hotel) => ({
        name: hotel.item.name,
        htuKey: hotel.htuKey,
      }));
    setPageHotelsForAssistant(chatKey, ordered);
    if (!activeHotel) {
      setActiveHotelForAssistant(chatKey, null);
    } else {
      const activeKey = getOriginalHotelKey(activeHotel.htuKey);
      const index = ordered.findIndex(
        (hotel) => hotel.htuKey === activeHotel.htuKey || getOriginalHotelKey(hotel.htuKey) === activeKey,
      );
      setActiveHotelForAssistant(chatKey, {
        name: activeHotel.item.name,
        htuKey: activeHotel.htuKey,
        index: index >= 0 ? index + 1 : null,
      });
    }
    return () => {
      setPageHotelsForAssistant(chatKey, []);
      setActiveHotelForAssistant(chatKey, null);
    };
  }, [hotels, pinnedHotelsIds, hotelIndex, activeHotel]);

  useEffect(() => {
    const chatKey = hotelIndex ?? 0;
    if (!hotels?.length || !hasPendingHotelCardAction(chatKey)) {
      return;
    }

    const pending = peekPendingHotelCardAction(chatKey);
    if (!pending) {
      return;
    }

    const hotel = findHotelForCardAction(
      hotels,
      pending as HotelCardActionPayload,
      activeHotel,
      pinnedHotelsIds,
    );
    if (!hotel) {
      takePendingHotelCardAction(chatKey);
      return;
    }

    takePendingHotelCardAction(chatKey);

    let hotelsForAction = hotels;
    let targetHotel = hotel;
    if (hotel.visible === false) {
      hotelsForAction = revealHotelForCardAction(hotels, hotel);
      targetHotel = hotelsForAction[0];
      setHotels(hotelsForAction);
      setActiveHotel(targetHotel);
    }

    const hotelDomId = targetHotel.htuKey;
    const hotelKey = getOriginalHotelKey(targetHotel.htuKey);
    const isCardMounted = () =>
      !!document.getElementById(hotelDomId) ||
      !!document.querySelector(`[id^="${CSS.escape(hotelKey)}"]`);

    const needsDom = cardActionNeedsDom(
      (pending as HotelCardActionPayload).action,
    );
    if (!isCardMounted() && targetHotel.visible !== false) {
      const reordered = bringHotelToFront(hotelsForAction, targetHotel);
      if (reordered) {
        hotelsForAction = reordered;
        setHotels(reordered);
      }
      setActiveHotel(targetHotel);
    }

    const run = () => {
      applyHotelCardAction(
        pending as HotelCardActionPayload,
        hotelsForAction,
        {
          setActiveHotel,
          setPinnedHotelsIds,
          pinnedHotelsIds,
          setCentered,
          setFocusedHotel,
          setActiveTab,
          setAreActiveRooms,
          showRoomsInTab: userSiteConfig.hotel.showRoomsInTab,
          displayStyle: userSiteConfig.hotel.displayStyle,
          toggleHotelProposal: (h) => {
            void toggleHotelProposal(h as any);
          },
          toggleRoomProposal: (h, room) => {
            void toggleRoomProposal(h as any, room as any);
          },
          goToDetails: ({ hotel: h, room }) => {
            void goToDetails({ hotel: h, room });
          },
          onCardLocalAction: ({ kind, hotel: h, room, show }) => {
            publishLocalHotelCardAction({
              kind,
              hotelKey: getOriginalHotelKey(h.htuKey),
              roomKey: room?.rcUniqueKey,
              show,
            });
          },
        },
        activeHotel,
      );
      scrollHotelCardIntoView(hotelDomId);
    };

    if (!needsDom || isCardMounted()) {
      run();
      return;
    }

    let tries = 0;
    const waitForCard = window.setInterval(() => {
      tries += 1;
      if (isCardMounted() || tries >= 25) {
        window.clearInterval(waitForCard);
        run();
      }
    }, 50);

    return () => window.clearInterval(waitForCard);
  }, [cardActionVersion, hotels?.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const goToDetails = async ({ hotel, room, filterRoom }: GoToRoomDetailsParams) => {
    const searchToken = sessionStorage.getItem(getKey('searchTokenHotels', hotelIndex))!;

    const multiRoomsBTokens = getRoomsBTokensForDetails(
      filter!,
      hotel,
      userSiteConfig.hotel.oneHotelSearchByOneSupplier ? room.htuKey : ''
    );

    let filterMeal = filter?.meal.value?.length ? filter?.meal.value.join(',') : '';
    if (filterRoom?.meal.value?.length) {
      filterMeal = filterRoom?.meal.value.join(',');
    }
    const search = new URLSearchParams({
      id: room.htuKey,
      session: searchToken,
      roomBToken: room.hotelRooms[0].bToken,
      roomPrice: room.price.usd + '',
      roomName: room.hotelRooms[0].displayName,
      roomCxl: room.cxl.limitDate?.split?.('T')[0],
      roomSupp: room.suppCode,
      checkIn: room.checkIn,
      checkOut: room.checkOut,
      nights: room.nights + '',
      maxOccupancy: getMaxOccupancy(room.hotelRooms[0]),
      filterBoarding: filter?.additional.withMeal || filterRoom?.boarding ? '1' : '0',
      filterAccessible: filter?.additional.ada || filterRoom?.accessible ? '1' : '0',
      filterRefund: filter?.additional.freeCxl || filterRoom?.refund ? '1' : '0',
      filterMeal,
    });

    sessionStorage.setItem(
      getKey('multiRoomsBTokens', hotelIndex),
      JSON.stringify(multiRoomsBTokens)
    );

    sessionStorage.setItem(
      getKey('hotelFromSearch', hotelIndex),
      JSON.stringify({
        ...hotel,
        roomClasses: [...hotel.roomClasses].splice(0, 1),
      })
    );
    pushImpression(hotels || []);

    if (b2b && !openDetailsInNewTab) {
      const currentParams = Object.fromEntries(Array.from(searchParams.entries()));
      setSearchParams({ ...currentParams, details: search.toString() });
    } else {
      const b2cParams = new URLSearchParams({
        checkIn: room.checkIn,
        checkOut: room.checkOut,
        details: new URLSearchParams({
          nights: room.nights + '',
          maxOccupancy: getMaxOccupancy(room.hotelRooms[0]),
        }).toString(),
      }).toString();

      const url = `/hotel${getHotelIndex(hotelIndex)}/details?${search.toString()}&${b2cParams}`;
      if (openDetailsInNewTab) {
        openInNewWindow(url);
      } else {
        navigate(url);
      }
    }
  };

  const visibleHotels = useMemo(
    () => hotels?.filter((h) => h.visible) || [],
    [hotels],
  );

  const hotelResultBody = (
    <>
      {(!!hotels?.length || poolingFinished) && (
        <div className="hotel-list-wr" data-assistant-target="list">
          <HotelsList
            hotels={visibleHotels}
            sortBy={sortByState || defaultSortByIfEmpty}
            goToDetails={goToDetails}
          />
        </div>
      )}
      {isMainLoading && <SkeletonHotelsList />}
      <ResultHotelConfig
        hotels={hotels}
        onFilterChange={onFilterChange}
        onLocationChange={onLocationChange}
        isMainLoading={isMainLoading}
        poolingFinished={poolingFinished}
        goToDetails={goToDetails}
        stat={stat}
        hideTabs={isModernDisplayMode}
      />
    </>
  );

  return (
    <div className={'hotel-result' + (isModernDisplayMode ? ' hotel-result-modern' : '')}>
      <MapHotelContext.Provider
        value={{
          setCentered,
          centered,
          focusedHotel,
          setFocusedHotel,
          initedMap,
          setInitedMap,
          currency,
          setCurrency,
          activeTab,
          setActiveTab,
          activeHotel,
          setActiveHotel,
          pinnedHotelsIds,
          setPinnedHotelsIds,
          geoPolygons,
          priceMode,
          setPriceMode,
          setGh,
          setTa,
          gh,
          ta,
          areActiveRooms,
          setAreActiveRooms,
          resetLocation,
          setResetLocation,
        }}
      >
        <HotelFilterContext.Provider
          value={{ filter, setFilter, neighborhoodList, setNeighborhoodList }}
        >
          <AssistantPageBridge
            onChangeFilter={onFilterChange}
            onSortChange={onSortChange}
            currentSortBy={sortByState}
          />
          <div className="hotel-result-header">
            <SearchHotel loadingSearch={!poolingFinished} onClickStop={onClickStop} />
            {!isMainLoading && (
              <FilterResults
                hotels={hotels}
                onFilterChange={onFilterChange}
                onSortChange={onSortChange}
                onLocationChange={onLocationChange}
                totalHotelsCount={total}
                availableBoardTypes={availableBoardTypes || []}
                loadingSearch={!poolingFinished}
                sortBy={sortByState}
              />
            )}
            {isMainLoading && <ResultsFilterSkeleton />}
          </div>
          {!response?.sessionExpired && searchParams.get('needToSearch') !== 'false' && (
            <div
              className={
                'hotel-result-data' +
                (activeTab === 6 ? ' hotel-result-data-htl' : '') +
                (activeTab === 0 ? ' hotel-result-data-map' : '') +
                ([1, 4, 5].includes(activeTab) ? ' hotel-result-data-rooms' : '')
              }
              style={
                isModernDisplayMode
                  ? ({ '--hotel-left-width': `${leftPanelWidth}px` } as CSSProperties)
                  : undefined
              }
            >
              {isModernDisplayMode && (
                <>
                  <FilterResultLeft hotels={hotels} onChangeFilter={onFilterChange} />
                  <ResultSplitter
                    width={leftPanelWidth}
                    onChange={setLeftPanelWidth}
                    onReset={resetLeftPanelWidth}
                  />
                </>
              )}
              {isModernDisplayMode ? (
                <div className="hotel-result-main">
                  <ResultHotelTabs
                    hotels={hotels}
                    isMainLoading={isMainLoading}
                    poolingFinished={poolingFinished}
                    stat={stat}
                  />
                  <div className="hotel-result-main-body">{hotelResultBody}</div>
                </div>
              ) : (
                hotelResultBody
              )}
            </div>
          )}
        </HotelFilterContext.Provider>
      </MapHotelContext.Provider>
      {showExpiredModal && (
        <ExpiredTimeout navigatePath="/hotels" onClose={() => setShowExpiredModal(false)} />
      )}
    </div>
  );
};

export default withErrorBoundary(ResultPage, 'ResultPage');
